import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchCategories } from './api';
import { categoryLabel } from './categories';

const COUNTRIES = [
  { code: 'IT', label: 'Italia' }, { code: 'DE', label: 'Germania' },
  { code: 'FR', label: 'Francia' }, { code: 'ES', label: 'Spagna' },
  { code: 'GB', label: 'Regno Unito' },
];

const PLATFORMS = [
  ['facebook', 'Facebook'],
  ['instagram', 'Instagram'],
  ['audience_network', 'Audience Network'],
  ['messenger', 'Messenger'],
];

const CONTEXT_PARAMS = ['q', 'pageId', 'pageName'];

export default function ArchiveFilters({ alwaysOpen = false }: { alwaysOpen?: boolean }) {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(alwaysOpen);
  const [draft, setDraft] = useState(() => Object.fromEntries(params.entries()));
  const categories = useQuery({ queryKey: ['categories'], queryFn: fetchCategories });

  useEffect(() => setDraft(Object.fromEntries(params.entries())), [params]);
  useEffect(() => { if (alwaysOpen) setOpen(true); }, [alwaysOpen]);

  const set = (name: string, value: string) => setDraft((current) => ({ ...current, [name]: value }));
  const invalidRange = Boolean(draft.from && draft.to && draft.from > draft.to);

  function apply(event: FormEvent) {
    event.preventDefault();
    if (invalidRange) return;
    const next = new URLSearchParams();
    Object.entries(draft).forEach(([key, value]) => value && next.set(key, value));
    setParams(next);
    if (!alwaysOpen) setOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function clear() {
    const next = new URLSearchParams();
    CONTEXT_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) next.set(key, value);
    });
    setDraft(Object.fromEntries(next.entries()));
    setParams(next);
    if (!alwaysOpen) setOpen(false);
  }

  const activeCount = [...params.keys()].filter((key) => !CONTEXT_PARAMS.includes(key)).length;

  return (
    <section className={alwaysOpen ? 'filter-panel is-persistent' : 'filter-panel'} aria-label="Filtri annunci">
      {!alwaysOpen && <button type="button" className="filter-trigger" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span>Filtri {activeCount > 0 && <strong>{activeCount}</strong>}</span>
        <span aria-hidden="true">{open ? '−' : '+'}</span>
      </button>}

      {open && <form className="filter-form" onSubmit={apply}>
        {!alwaysOpen && <header className="filter-sheet-head"><div><p className="eyebrow">Annunci</p><h2>Tutti i filtri</h2></div><button type="button" aria-label="Chiudi filtri" onClick={() => setOpen(false)}>×</button></header>}

        <section className="filter-group">
          <h2>Annuncio</h2>
          <div className="filter-grid">
            <label>Stato<select value={draft.active ?? ''} onChange={(event) => set('active', event.target.value)}><option value="">Tutti</option><option value="true">Attivi</option><option value="false">Conclusi</option></select></label>
            <label>Categoria<select value={draft.category ?? ''} onChange={(event) => set('category', event.target.value)}><option value="">Tutte</option>{(categories.data ?? []).map((category) => <option key={category.category} value={category.category}>{categoryLabel(category.category)} ({category.count})</option>)}</select></label>
            <label>Piattaforma<select value={draft.platform ?? ''} onChange={(event) => set('platform', event.target.value)}><option value="">Tutte</option>{PLATFORMS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label>Ordina per<select value={draft.sort ?? 'recent'} onChange={(event) => set('sort', event.target.value)}><option value="recent">Visti di recente</option><option value="reach">Reach EU</option><option value="days">Longevità</option><option value="startTime">Data di partenza</option></select></label>
          </div>
        </section>

        <details className="filter-advanced">
          <summary><span><strong>Filtri avanzati</strong><small>Pubblico, periodo e performance</small></span><span aria-hidden="true">+</span></summary>
          <div>
            <section className="filter-group">
              <h2>Pubblico</h2>
              <div className="filter-grid">
                <label>Località target<input value={draft.location ?? ''} onChange={(event) => set('location', event.target.value)} placeholder="es. Trieste" /></label>
                <label>Paese raggiunto<select value={draft.country ?? ''} onChange={(event) => set('country', event.target.value)}><option value="">Tutti</option>{COUNTRIES.map((country) => <option key={country.code} value={country.code}>{country.label}</option>)}</select></label>
                <label>Genere<select value={draft.gender ?? ''} onChange={(event) => set('gender', event.target.value)}><option value="">Tutti</option><option value="Women">Donne</option><option value="Men">Uomini</option><option value="All">Pubblico misto</option></select></label>
                <label>Età target<input type="number" min="13" max="99" value={draft.age ?? ''} onChange={(event) => set('age', event.target.value)} placeholder="es. 30" /></label>
                <label>Lingua<input value={draft.language ?? ''} onChange={(event) => set('language', event.target.value)} maxLength={5} placeholder="es. it" /></label>
              </div>
            </section>
            <section className="filter-group">
              <h2>Periodo e performance</h2>
              <div className="filter-grid">
                <label>Dal<input type="date" value={draft.from ?? ''} onChange={(event) => set('from', event.target.value)} /></label>
                <label>Al<input type="date" value={draft.to ?? ''} onChange={(event) => set('to', event.target.value)} /></label>
                <label>Reach minimo<input type="number" min="0" step="100" value={draft.minReach ?? ''} onChange={(event) => set('minReach', event.target.value)} placeholder="es. 1000" /></label>
                <label>In onda da almeno<select value={draft.minDays ?? ''} onChange={(event) => set('minDays', event.target.value)}><option value="">Qualsiasi durata</option><option value="7">7 giorni</option><option value="30">30 giorni</option><option value="60">60 giorni</option></select></label>
                <label>Varianti creative<select value={draft.minVariants ?? ''} onChange={(event) => set('minVariants', event.target.value)}><option value="">Qualsiasi</option><option value="2">Almeno 2 (A/B test)</option><option value="3">Almeno 3</option></select></label>
                <label>Nuovi in archivio<select value={draft.newDays ?? ''} onChange={(event) => set('newDays', event.target.value)}><option value="">Da sempre</option><option value="2">48 ore</option><option value="7">7 giorni</option><option value="30">30 giorni</option></select></label>
              </div>
              {invalidRange && <p className="error">La data finale deve essere successiva a quella iniziale.</p>}
            </section>
          </div>
        </details>

        <div className="filter-actions">
          <button type="button" className="btn-secondary" onClick={clear}>Azzera filtri</button>
          <button type="submit" className="btn-primary" disabled={invalidRange}>Applica filtri</button>
        </div>
      </form>}
    </section>
  );
}
